import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  BarChart3, 
  Wifi, 
  WifiOff, 
  AlertTriangle, 
  RefreshCw, 
  Play, 
  Pause,
  Settings,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  HelpCircle,
  Loader2,
  TrendingUp
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiService } from '@/services/api';
import { cn } from '@/lib/utils';

interface WhatsappConnection {
  id: number;
  name: string;
  chatconnect_account_id: string;
  client_id: number;
  isOnline: boolean;
  status: 'connected' | 'disconnected' | 'error' | 'unknown';
  lastCheck: string;
  hasMonitoring: boolean;
}

interface MonitoringStats {
  total: number;
  online: number;
  offline: number;
  unknown: number;
  withMonitoring: number;
}

interface MonitoringStatus {
  isActive: boolean;
  frequency: string;
  nextCheck: string;
}

interface QueueStatus {
  clientId: number;
  clientName: string;
  status: 'running' | 'paused_limit' | 'paused_schedule' | 'inactive';
  reason: string;
  dailyCount: number;
  dailyLimit: number | null;
  schedule: string | null;
  rateLimit: string | null;
}

const WhatsappMonitoringBoard = () => {
  const [connections, setConnections] = useState<WhatsappConnection[]>([]);
  const [stats, setStats] = useState<MonitoringStats | null>(null);
  const [monitoringStatus, setMonitoringStatus] = useState<MonitoringStatus | null>(null);
  const [queues, setQueues] = useState<QueueStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const { toast } = useToast();

  // Carregar dados iniciais
  useEffect(() => {
    loadMonitoringData();
  }, []);

  const loadMonitoringData = async () => {
    try {
      setIsLoading(true);
      
      // Carregar status do monitoramento
      const statusResponse = await apiService.get('/monitoring/whatsapp/status');
      if (statusResponse.success) {
        setMonitoringStatus(statusResponse.monitoring);
      }

      // Carregar conexões
      const connectionsResponse = await apiService.get('/monitoring/whatsapp/connections');
      if (connectionsResponse.success) {
        setConnections(connectionsResponse.connections);
        setStats(connectionsResponse.summary);
      }

      // Carregar dados de limite diário e progresso
      const queuesResponse = await apiService.get('/monitoring/queues-status');
      if (queuesResponse.success) {
        setQueues(queuesResponse.data);
      }

    } catch (error) {
      console.error('Erro ao carregar dados de monitoramento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os dados de monitoramento",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    await loadMonitoringData();
    setIsRefreshing(false);
    toast({
      title: "Atualizado",
      description: "Dados de monitoramento atualizados"
    });
  };

  const handleToggleMonitoring = async () => {
    const isActive = monitoringStatus?.isActive;
    const action = isActive ? stopMonitoring : startMonitoring;
    await action();
  };

  const startMonitoring = async () => {
    try {
      setIsStarting(true);
      const response = await apiService.post('/monitoring/whatsapp/start');
      
      if (response.success) {
        toast({
          title: "Monitoramento Iniciado",
          description: "O monitoramento automático foi iniciado com sucesso"
        });
        await loadMonitoringData();
      }
    } catch (error) {
      console.error('Erro ao iniciar monitoramento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível iniciar o monitoramento",
        variant: "destructive"
      });
    } finally {
      setIsStarting(false);
    }
  };

  const stopMonitoring = async () => {
    try {
      setIsStopping(true);
      const response = await apiService.post('/monitoring/whatsapp/stop');
      
      if (response.success) {
        toast({
          title: "Monitoramento Parado",
          description: "O monitoramento automático foi parado com sucesso"
        });
        await loadMonitoringData();
      }
    } catch (error) {
      console.error('Erro ao parar monitoramento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível parar o monitoramento",
        variant: "destructive"
      });
    } finally {
      setIsStopping(false);
    }
  };

  const checkConnection = async (connectionId: number) => {
    try {
      const response = await apiService.post(`/monitoring/whatsapp/check/${connectionId}`);
      
      if (response.success) {
        toast({
          title: "Verificação Realizada",
          description: `Conexão ${response.connection.name} verificada com sucesso`
        });
        await loadMonitoringData();
      }
    } catch (error) {
      console.error('Erro ao verificar conexão:', error);
      toast({
        title: "Erro",
        description: "Não foi possível verificar a conexão",
        variant: "destructive"
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
        return <Wifi className="w-4 h-4 text-green-500" />;
      case 'disconnected':
        return <WifiOff className="w-4 h-4 text-red-500" />;
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default:
        return <HelpCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'disconnected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'error':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'connected':
        return 'Conectado';
      case 'disconnected':
        return 'Desconectado';
      case 'error':
        return 'Erro';
      default:
        return 'Desconhecido';
    }
  };

  const formatLastCheck = (dateString: string) => {
    if (!dateString) return 'Nunca verificado';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Agora mesmo';
    if (diffMins < 60) return `${diffMins} min atrás`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h atrás`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} dias atrás`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Carregando monitoramento...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-8 h-8 text-blue-600" />
            Monitoramento WhatsApp
          </h1>
          <p className="text-gray-600 mt-1">Status em tempo real das conexões com o WhatsApp.</p>
        </div>
        <div className="flex items-center gap-4">
          <Button 
            onClick={refreshData} 
            variant="outline"
            disabled={isRefreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>

          <Button
            onClick={handleToggleMonitoring}
            disabled={isStarting || isStopping}
            className={cn(
              "flex items-center gap-2",
              monitoringStatus?.isActive ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"
            )}
          >
            {monitoringStatus?.isActive ? (
              <>{isStopping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4" />} Parar Monitoramento</>
            ) : (
              <>{isStarting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Iniciar Monitoramento</>
            )}
          </Button>
        </div>
      </div>

      {/* Status do Monitoramento */}
      {monitoringStatus && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Status do Monitoramento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-3 h-3 rounded-full",
                  monitoringStatus.isActive ? "bg-green-500" : "bg-red-500"
                )} />
                <div>
                  <p className="text-sm font-medium">Status</p>
                  <p className="text-sm text-gray-600">
                    {monitoringStatus.isActive ? 'Ativo' : 'Inativo'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Frequência</p>
                  <p className="text-sm text-gray-600">{monitoringStatus.frequency}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <BarChart3 className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium">Próxima Verificação</p>
                  <p className="text-sm text-gray-600">{monitoringStatus.nextCheck}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Estatísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-gray-600">Total de Conexões</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.online}</p>
                  <p className="text-sm text-gray-600">Online</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <XCircle className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.offline}</p>
                  <p className="text-sm text-gray-600">Offline</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">{stats.withMonitoring}</p>
                  <p className="text-sm text-gray-600">Com Monitoramento</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reset Automático Diário - Limite e Progresso */}
      {queues && queues.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Reset Automático Diário
            </CardTitle>
            <p className="text-sm text-gray-600">Serviço que reseta automaticamente os contadores de rate limit à meia-noite</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm font-medium">Status: Ativo</span>
                </div>
                <div className="text-sm text-gray-500">
                  Próxima verificação: 5 min
                </div>
              </div>
              
              {/* Estatísticas de limite diário por cliente */}
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Progresso de Envios por Cliente:</h4>
                {queues.map((queue) => (
                  <div key={queue.clientId} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{queue.clientName}</span>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          queue.status === 'running' ? 'bg-green-100 text-green-800' :
                          queue.status === 'paused_limit' ? 'bg-red-100 text-red-800' :
                          queue.status === 'paused_schedule' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {queue.status === 'running' ? 'Ativo' :
                           queue.status === 'paused_limit' ? 'Limite Atingido' :
                           queue.status === 'paused_schedule' ? 'Fora do Horário' :
                           'Inativo'}
                        </Badge>
                      </div>
                    </div>
                    
                    {queue.dailyLimit !== null && (
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-gray-600">Limite Diário</span>
                          <span className="text-sm font-bold">
                            {queue.dailyCount} / {queue.dailyLimit}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${
                              (queue.dailyCount / queue.dailyLimit) >= 1 ? 'bg-red-500' :
                              (queue.dailyCount / queue.dailyLimit) >= 0.8 ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}
                            style={{ 
                              width: `${Math.min(100, (queue.dailyCount / queue.dailyLimit) * 100)}%` 
                            }}
                          />
                        </div>
                        <div className="text-xs text-gray-500">
                          {Math.round((queue.dailyCount / queue.dailyLimit) * 100)}% utilizado
                        </div>
                      </div>
                    )}
                    
                    {queue.dailyLimit === null && (
                      <div className="text-xs text-gray-500">
                        Sem limite diário configurado
                      </div>
                    )}
                    
                    {queue.schedule && (
                      <div className="text-xs text-gray-500 mt-2">
                        Horário: {queue.schedule}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de Conexões */}
      <Card>
        <CardHeader>
          <CardTitle>Conexões WhatsApp</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {connections.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhuma conexão encontrada
              </div>
            ) : (
              connections.map((connection) => (
                <div
                  key={connection.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {getStatusIcon(connection.status)}
                    
                    <div>
                      <h3 className="font-medium text-gray-900">{connection.name}</h3>
                      <p className="text-sm text-gray-500">
                        ID: {connection.chatconnect_account_id}
                      </p>
                      <p className="text-xs text-gray-400">
                        Última verificação: {formatLastCheck(connection.lastCheck)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Badge className={getStatusColor(connection.status)}>
                      {getStatusText(connection.status)}
                    </Badge>
                    
                    {connection.hasMonitoring && (
                      <Button
                        onClick={() => checkConnection(connection.id)}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" />
                        Verificar
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatsappMonitoringBoard; 